import React, { memo, useMemo } from 'react';
import { BudgetBys } from 'business/constants';
import { useTranslation } from 'react-i18next';
import {
    NATURE_FIELD,
    GESTIONNAIRE_FIELD,
    ORG_CHART_FINANCE_FIELD,
} from 'routes/BudgetModule/config/constants';
import { styled, SxProps } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
    BudgetProposalT,
    BudgetProposalSettingsT,
} from 'routes/BudgetModule/config/types';
import PivotGrid from 'components/PivotGrid/PivotGrid';
import { checkBoxEnabledStyle, style } from 'components/PivotGrid/defaultStyle';
import { PivotFieldsAggridT } from 'business/definitions';
import { getFieldForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { generateGetDimensionColumn } from '../helpers/pivotGrid';
import { useMutateBudgetProposalSettings } from '../../ExpenseIncome/hooks/budgetProposalSettings';
import { AgGridReactProps } from 'ag-grid-react';
import { SideBarDef } from 'ag-grid-community';
import getInitialColDefs from '../getInitialColDefs';
import Loading from 'components/Loading';
import { useDebounce } from 'hooks/helpers';
import { ProjectWithInscriptionT } from '../hooks/getProjectsWithInscriptions';
import AmountWithInscriptionsButtonCellRenderer from 'routes/BudgetModule/components/shared/BudgetChart/BudgetPivotGrid/AmountWithInscriptionsButtonCellRenderer';
import { translateBlockValue } from 'i18n';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { useParams } from 'react-router-dom';
import { getResourceType } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { useFullScreen } from 'contexts/FullScreenContext';

const DEBOUNCE_TIME = 2000;

const SIDE_BAR: SideBarDef = {
    toolPanels: [
        {
            id: 'columns',
            labelKey: 'columns',
            labelDefault: 'sideBar',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressPivotMode: true,
            },
        },
    ],
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    width: '100%',
    '& .mui-pivot-grid-wrapper': {
        ...style,
        ...checkBoxEnabledStyle,
    },
    '& .ag-theme-material': { width: '100%', fontFamily: 'Roboto' },
});

type Props = {
    data: ProjectWithInscriptionT[] | undefined;
    isLoading: boolean;
    budgetProposal: BudgetProposalT;
    settings: PivotFieldsAggridT | undefined;
    baseFilters: QueryFilterT[];
    setExporterToExcel: (arg1: () => void) => void;
    sx?: SxProps;
};

const BudgetProjectsPivotGrid = ({
    data,
    isLoading,
    budgetProposal,
    settings,
    baseFilters,
    setExporterToExcel,
    sx = {},
}: Props): React.ReactElement => {
    const { isFullScreen } = useFullScreen();
    const { resourceSoftwareId = '' } = useParams();
    const resourceType = getResourceType(budgetProposal.by);
    const queryClient = useQueryClient();
    const { data: integratedSoftwares, isLoading: areSoftwaresLoading } =
        useIntegrationSoftwares();
    const GRID_PROPS: Pick<
        AgGridReactProps,
        | 'sideBar'
        | 'pagination'
        | 'paginationPageSize'
        | 'components'
        | 'processPivotResultColGroupDef'
    > = {
        sideBar: SIDE_BAR,
        pagination: !isFullScreen,
        paginationPageSize: 10,
        components: {
            amountWithInscriptionsButtonCellRenderer:
                AmountWithInscriptionsButtonCellRenderer,
        },
        processPivotResultColGroupDef: (colDef) =>
            (colDef.headerName = translateBlockValue(
                colDef.headerName ?? '',
                true,
            )),
    };
    const { t } = useTranslation();
    const { by, phase } = budgetProposal;

    const amountRequestedField = getFieldForPhase(phase, 'amount');

    const visibleDimensionsByDefault = useMemo(
        () =>
            by === BudgetBys.GESTIONNAIRE
                ? [GESTIONNAIRE_FIELD, NATURE_FIELD]
                : [ORG_CHART_FINANCE_FIELD, NATURE_FIELD],
        [by],
    );

    const getDimensionColumn = useMemo(
        () => generateGetDimensionColumn(visibleDimensionsByDefault),
        [visibleDimensionsByDefault],
    );

    const { projectKeys } = createBudgetQueryKeys();
    const projectCacheKey = projectKeys({
        filters: baseFilters,
        budget_by: budgetProposal.by,
    });

    const onInscriptionsModalClosed = () => {
        queryClient.invalidateQueries({ queryKey: projectCacheKey });
    };

    // we need to memoize initialColDef to avoid useless PivotGrid re renders
    // if we don't do this column state changes are not taken into account
    const initialColDefs = useMemo(
        () =>
            getInitialColDefs({
                amountRequestedField,
                integratedSoftwares,
                getDimensionColumn,
                budgetProposal,
                baseFilters,
                onInscriptionsModalClosed,
            }),
        [amountRequestedField, integratedSoftwares, areSoftwaresLoading],
    );

    const { mutate: mutateAnalyseSettings } = useMutateBudgetProposalSettings(
        budgetProposal,
        resourceType,
        resourceSoftwareId,
    );
    const updateSettings = (pivotFieldsAggrid: PivotFieldsAggridT) => {
        const body: Partial<BudgetProposalSettingsT> = {
            projectsGrid: {
                columns: pivotFieldsAggrid,
            },
        };
        mutateAnalyseSettings(body);
    };

    const debouncedMutateSettings = useDebounce(updateSettings, DEBOUNCE_TIME);

    if (isLoading && areSoftwaresLoading) {
        return <Loading />;
    } else if (!data || !settings) {
        return <div>{t('budgetProposal.noData')}</div>;
    } else {
        return (
            <StyGrid sx={sx}>
                <Box
                    className="mui-pivot-grid-wrapper"
                    sx={{ height: '100', width: '100%' }}
                >
                    <PivotGrid
                        className="mui-project-pivot-grid"
                        // @ts-expect-error [review typing]
                        data={data}
                        initialColDefs={initialColDefs}
                        pivotFieldsAggrid={settings}
                        onColumnsChanged={debouncedMutateSettings}
                        gridProps={GRID_PROPS}
                        groupIncludeTotalFooter={false}
                        setExporterToExcel={setExporterToExcel}
                    />
                </Box>
            </StyGrid>
        );
    }
};

export default memo(BudgetProjectsPivotGrid);
